import React, { Suspense, lazy } from 'react';
import {
  createBrowserRouter,
  createRoutesFromElements,
  Route,
} from 'react-router-dom';

import { action as loginAction } from './pages/auth/Login';
import { action as signUpAction } from './pages/auth/Signup';

// Core structural layouts - loaded immediately
import HomeLayout from './layout/HomeLayout';
import { Layout } from './layout/Layout';
import GlobalLayout from './layout/GlobalLayout';
import Search from './pages/Search';

// Lazy loaded page components
const AboutUS = lazy(() => import('./pages/AboutUS/AboutUS'));
const Login = lazy(() => import('./pages/auth/Login'));
const Signup = lazy(() => import('./pages/auth/Signup'));
const AuthAction = lazy(() => import('./pages/auth/AuthAction'));
const BookMarks = lazy(() => import('./pages/BookMarks'));
const Vows = lazy(() => import('./pages/Vows/Vows'));
const BahishkritBharat = lazy(() => import('./pages/BahishkritBharat'));
const MindMapReact = lazy(() => import('./pages/MindMap'));
const Books = lazy(() => import('./pages/Books/Books'));
const BookView = lazy(() => import('./pages/BookView/BookView'));
const BahishkritBharatArticle = lazy(
  () => import('./pages/BahishkritBharatArticle')
);
const ProfilePage = lazy(() => import('./pages/Profile/ProfilePage'));
const SwarajCritiqueArticles = lazy(
  () => import('./pages/SwarajCritiqueArticles')
);
const SwarajCritique = lazy(() => import('./pages/SwarajCritique'));
const LetterArchive = lazy(() => import('./pages/Letters'));
const AmbedkarQuoteBrowser = lazy(
  () => import('./component/AmbedkarQuoteBrowser')
);

// A simple loading fallback
const PageLoader = () => (
  <div className="w-full h-full flex items-center justify-center min-h-[50vh]">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
  </div>
);

export const router = createBrowserRouter(
  createRoutesFromElements(
    <Route element={<GlobalLayout />}>
      <Route element={<Layout />}>
        <Route path="/" element={<HomeLayout />}>
          <Route
            index
            element={
              <Suspense fallback={<PageLoader />}>
                <Books />
              </Suspense>
            }
          />
          <Route path="search" element={<Search />} />
          <Route
            path="bookmarks"
            element={
              <Suspense fallback={<PageLoader />}>
                <BookMarks />
              </Suspense>
            }
          />
          <Route
            path="vows"
            element={
              <Suspense fallback={<PageLoader />}>
                <Vows />
              </Suspense>
            }
          />
          <Route
            path="letters"
            element={
              <Suspense fallback={<PageLoader />}>
                <LetterArchive />
              </Suspense>
            }
          />
          <Route
            path="bahishkrit_bharat"
            element={
              <Suspense fallback={<PageLoader />}>
                <BahishkritBharat />
              </Suspense>
            }
          />
          <Route
            path="swaraj-critique"
            element={
              <Suspense fallback={<PageLoader />}>
                <SwarajCritiqueArticles />
              </Suspense>
            }
          />
          <Route
            path="mindmap"
            element={
              <Suspense fallback={<PageLoader />}>
                <MindMapReact />
              </Suspense>
            }
          />
          <Route
            path="listen"
            element={<div id="audio-player-target" className="h-full w-full" />}
          />
          <Route
            path="quotes"
            element={
              <Suspense fallback={<PageLoader />}>
                <AmbedkarQuoteBrowser />
              </Suspense>
            }
          />
        </Route>
        <Route
          path="about"
          element={
            <Suspense fallback={<PageLoader />}>
              <AboutUS />
            </Suspense>
          }
        />
        <Route
          path="profile"
          element={
            <Suspense fallback={<PageLoader />}>
              <ProfilePage />
            </Suspense>
          }
        />
        <Route
          path="login"
          element={
            <Suspense fallback={<PageLoader />}>
              <Login />
            </Suspense>
          }
          action={loginAction}
        />
        <Route
          path="signup"
          element={
            <Suspense fallback={<PageLoader />}>
              <Signup />
            </Suspense>
          }
          action={signUpAction}
        />
        <Route
          path="auth/action"
          element={
            <Suspense fallback={<PageLoader />}>
              <AuthAction />
            </Suspense>
          }
        />
        <Route
          path="*"
          element={
            <Suspense fallback={<PageLoader />}>
              <BookView />
            </Suspense>
          }
        />

        <Route path="books">
          <Route
            path=":bookParent/:language/swaraj-critique/:date/:article"
            element={
              <Suspense fallback={<PageLoader />}>
                <SwarajCritique />
              </Suspense>
            }
          />
          <Route
            path=":bookParent/:language/bahishkrit-bharat/:date/:article"
            element={
              <Suspense fallback={<PageLoader />}>
                <BahishkritBharatArticle />
              </Suspense>
            }
          />
        </Route>
      </Route>
      <Route path="books">
        <Route
          path=":bookParent/:language/:bookName/:pageType/:pageNo"
          element={
            <Suspense fallback={<PageLoader />}>
              <BookView />
            </Suspense>
          }
        />
        <Route
          path=":bookParent/:language/:bookName"
          element={
            <Suspense fallback={<PageLoader />}>
              <BookView />
            </Suspense>
          }
        />
      </Route>
    </Route>
  )
);
